import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  createProspectionQuery,
  getProspectionByUserIdQuery,
  getProspectionByClientAndProspectionIdsQuery,
  detachProspectionQuery,
  updateProspectionSectorQuery,
  updateProspectionTrafficLightQuery,
} from "./queries";
import { QUERY_STATUS } from "constants/constants";
import { useUserContext } from "api/user/UserProvider";

const ProspectionContext = createContext({});

export const useProspectionContext = () => useContext(ProspectionContext);

export const ProspectionProvider = ({ ...props }) => {
  const { userScope } = useUserContext();
  const [errorData, setErrorData] = useState({});
  const [createdProspection, setCreatedProspection] = useState({});
  const [createProspectionStatus, setCreateProspectionStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [prospectionList, setProspectionList] = useState([]);
  const [getProspectionStatus, setGetProspectionStatus] = useState(
    QUERY_STATUS.INITIAL
  );

  const releaseProspection = useCallback(
    async (prospectionId) => {
      try {
        await detachProspectionQuery({ id: prospectionId, scope: userScope });
      } catch (e) {
        console.error("releaseProspection", e);
        throw e;
      }
    },
    [userScope]
  );

  const createProspection = useCallback(async (data) => {
    try {
      const result = await createProspectionQuery(data);
      if (result) {
        setCreateProspectionStatus(QUERY_STATUS.SUCCESS);
        setCreatedProspection(result.prospection);
      }
    } catch (e) {
      setErrorData(e.response.data);
      setCreateProspectionStatus(QUERY_STATUS.ERROR);
      console.error("createProspection", e);
      throw e;
    }
  }, []);

  const userProspectionByUserIdGetter = useCallback(
    async ({ id }) => {
      try {
        const result = await getProspectionByUserIdQuery({
          id,
          scope: userScope,
        });
        const sortByDate = result?.sort((a, b) => {
          return new Date(b.attribution_date) - new Date(a.attribution_date);
        });
        return setProspectionList(sortByDate);
      } catch (e) {
        console.error("getProspectionByUserId", e);
        throw e;
      }
    },
    [userScope]
  );

  const getProspectionByClientAndProspectionIds = useCallback(
    async ({ client_ids, prospection_ids }) => {
      try {
        setGetProspectionStatus(QUERY_STATUS.LOADING);
        const result = await getProspectionByClientAndProspectionIdsQuery({
          client_ids,
          prospection_ids,
          userScope,
        });
        setProspectionList(result);
        setGetProspectionStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        console.error("getProspectionByClientAndProspectionIds", e);
        setGetProspectionStatus(QUERY_STATUS.ERROR);
        throw e;
      }
    },
    [userScope]
  );

  const updateProspectionSector = useCallback(
    async ({ id, sector_id, manager_id }) => {
      try {
        await updateProspectionSectorQuery({ id, sector_id });
        setProspectionList([]);
        await userProspectionByUserIdGetter({
          id: manager_id,
        });
      } catch (e) {
        console.error("updateProspectionSector", e);
        throw e;
      }
    },
    [userScope]
  );

  const updateProspectionTrafficLight = useCallback(
    async ({ id, traffic_light_value, manager_id }) => {
      try {
        await updateProspectionTrafficLightQuery({
          id,
          traffic_light_value,
        });
        setProspectionList([]);
        await userProspectionByUserIdGetter({
          id: manager_id,
        });
      } catch (e) {
        console.error("updateProspectionTrafficLight", e);
        throw e;
      }
    },
    [userScope]
  );

  const values = useMemo(
    () => ({
      updateProspectionSector,
      updateProspectionTrafficLight,
      releaseProspection,
      userScope,
      getProspectionByClientAndProspectionIds,
      createProspectionStatus,
      setCreateProspectionStatus,
      setCreatedProspection,
      createdProspection,
      createProspection,
      errorData,
      userProspectionByUserIdGetter,
      prospectionList,
      setProspectionList,
      getProspectionStatus,
      setGetProspectionStatus,
    }),
    [
      updateProspectionSector,
      updateProspectionTrafficLight,
      releaseProspection,
      userScope,
      getProspectionByClientAndProspectionIds,
      createProspectionStatus,
      setCreateProspectionStatus,
      setCreatedProspection,
      createdProspection,
      createProspection,
      errorData,
      userProspectionByUserIdGetter,
      prospectionList,
      setProspectionList,
      getProspectionStatus,
      setGetProspectionStatus,
    ]
  );
  return <ProspectionContext.Provider value={values} {...props} />;
};
